import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import apiClient from '@app/config/axios.config'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useFeatureFlags } from './useFeatureFlags'

export type AalReason =
  | 'no_damage_ratio_data'
  | 'insufficient_scenarios'
  | 'missing_replacement_cost'

export interface AalResponse {
  asset_id?: number
  hazard_id: string
  aal_min: number | null
  aal_max: number | null
  scenario_count?: number
  asset_count?: number
  missing_assets?: number
  reason?: AalReason
}

/**
 * Scenario-context filters: every non-AEP parameter on the current hazard
 * scenario (slr, year, intervention, development_scenario, …). The backend
 * holds these constant while integrating across AEP. The `aep` key is
 * stripped server-side so callers can pass `hazardLayer.parameters` directly.
 */
export type AalFilters = { [key: string]: string | number | number[] }

const STALE_TIME_MS = 1000 * 60 * 5

/** Imperative fetch — for callers that render to a one-shot HTML string (e.g. Mapbox popup). */
export const fetchAalForSelectedElement = async (
  element_layer_id: string,
  asset_id: number,
  hazard_id: string,
  filters: AalFilters,
): Promise<AalResponse> => {
  const res = await apiClient.post<AalResponse>(
    `/asset/aal/selected_element/${element_layer_id}/${asset_id}/${hazard_id}`,
    filters,
  )
  return res.data
}

const fetchAalForPortfolio = async (params: {
  element_layer_id: string
  hazard_id: string
  filters: AalFilters
  regions: string[] | null
  draw_area: number[] | null
}): Promise<AalResponse> => {
  const { element_layer_id, hazard_id, filters, regions, draw_area } = params
  const search = new URLSearchParams()
  if (regions && regions.length) search.set('regions', regions.join(','))
  if (draw_area && draw_area.length) search.set('draw_area', draw_area.join(','))
  const qs = search.toString() ? `?${search.toString()}` : ''
  const res = await apiClient.post<AalResponse>(
    `/asset/aal/portfolio/${element_layer_id}/${hazard_id}${qs}`,
    filters,
  )
  return res.data
}

/** AAL for a single (asset, hazard) — used by the selected-element card. */
export const useAalForSelectedElement = (params: {
  element_layer_id: string | undefined
  asset_id: number | undefined
  hazard_id: string | undefined
  filters?: AalFilters
}): UseQueryResult<AalResponse> => {
  const { useFeatureFlag } = useFeatureFlags()
  const enabled =
    useFeatureFlag(FEATURE_FLAGS.AAL_REPORTING) &&
    !!params.element_layer_id &&
    params.asset_id !== undefined &&
    !!params.hazard_id

  return useQuery({
    queryKey: [
      'aal',
      'selectedElement',
      params.element_layer_id,
      params.asset_id,
      params.hazard_id,
      params.filters ?? {},
    ],
    queryFn: () =>
      fetchAalForSelectedElement(
        params.element_layer_id as string,
        params.asset_id as number,
        params.hazard_id as string,
        params.filters ?? {},
      ),
    enabled,
    staleTime: STALE_TIME_MS,
  })
}

/** Per-hazard AAL aggregated across the visible element layer. Used by the risk summary tab. */
export const useAalForPortfolio = (params: {
  element_layer_id: string | undefined
  hazard_id: string | undefined
  filters?: AalFilters
  regions?: string[] | null
  draw_area?: number[] | null
}): UseQueryResult<AalResponse> => {
  const { useFeatureFlag } = useFeatureFlags()
  const enabled =
    useFeatureFlag(FEATURE_FLAGS.AAL_REPORTING) && !!params.element_layer_id && !!params.hazard_id

  return useQuery({
    queryKey: [
      'aal',
      'portfolio',
      params.element_layer_id,
      params.hazard_id,
      params.filters ?? {},
      params.regions ?? null,
      params.draw_area ?? null,
    ],
    queryFn: () =>
      fetchAalForPortfolio({
        element_layer_id: params.element_layer_id as string,
        hazard_id: params.hazard_id as string,
        filters: params.filters ?? {},
        regions: params.regions ?? null,
        draw_area: params.draw_area ?? null,
      }),
    enabled,
    staleTime: STALE_TIME_MS,
  })
}

/**
 * Hazard shape expected by the map hover tooltip — just enough to identify the
 * hazard and pass its scenario context to the AAL endpoint. Callers also use
 * other fields (vulnerability label, exposure, etc.) but those are unrelated
 * to the AAL enrichment performed here.
 */
type HoverHazard = {
  hazard_id?: string
  parameters?: AalFilters
}

/**
 * Fetches AAL for every hazard the asset is exposed to (in parallel) and
 * returns each hazard enriched with `aal_min`/`aal_max`/`aal_reason`. Failures
 * fall through to a `no_damage_ratio_data` N/A row so the tooltip still
 * surfaces the feature line rather than silently dropping it.
 *
 * Intended for the map hover popup, which is rendered to a static HTML string
 * via `ReactDOMServer.renderToString` and so can't use the React-Query hook.
 */
export const enrichHazardsWithAal = async <T extends HoverHazard>(
  element_layer_id: string,
  asset_id: number,
  hazards: T[],
): Promise<(T & { aal_min: number | null; aal_max: number | null; aal_reason?: AalReason })[]> => {
  const fallback = {
    aal_min: null,
    aal_max: null,
    reason: 'no_damage_ratio_data' as AalReason,
  }

  const results = await Promise.all(
    hazards.map((h) =>
      h.hazard_id
        ? fetchAalForSelectedElement(
            element_layer_id,
            asset_id,
            h.hazard_id,
            h.parameters ?? {},
          ).catch(() => fallback)
        : Promise.resolve(fallback),
    ),
  )

  return hazards.map((h, i) => ({
    ...h,
    aal_min: results[i].aal_min ?? null,
    aal_max: results[i].aal_max ?? null,
    aal_reason: results[i].reason,
  }))
}
